import {
  type Dispatch,
  type FC,
  type SetStateAction,
  Fragment,
  useEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { Check, ChevronDown, ChevronRight, Download, Search } from "lucide-react";
import {
  DashboardDataTablePanel,
  DashboardDataTablePanelHeader,
  DashboardDataTableScroll,
  DashboardDataTableSortButton,
  DashboardDataTableTable,
  DefinitionTooltip,
  TablePagePagination,
  dashboardDataTable as dt,
  type DefinitionTooltipInfo,
} from "src/components/common";
import { googleAdsAdNetworkChannelClass } from "../../utils/paidMediaTags";
import {
  displayFormattedCurrency,
  getGoogleColumnTooltip,
  getRoasToneClass,
  mapAdGroupApiItem,
  missingValue,
  type CampaignSummary,
  type GoogleAdsCampaignSortKey,
  type GoogleSectionHelpKey,
} from "./googleAdsTableConfig";
import { formatPaidMediaDateText, formatPaidMediaRoas } from "../../utils/paidMediaNumberFormat";

const FilterDropdown: FC<{
  value: string;
  options: Array<{ value: string; label: string }>;
  onChange: (_v: string) => void;
}> = ({ value, options, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const selectedOption = options.find(option => option.value === value) || options[0];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div
      ref={containerRef}
      className={`paid-media-filter-dropdown ${
        isOpen ? "paid-media-filter-dropdown--open" : ""
      }`}
    >
      <button
        type="button"
        className="paid-media-filter-chip paid-media-filter-dropdown__trigger"
        onClick={() => setIsOpen(current => !current)}
      >
        <span className="paid-media-filter-dropdown__label">
          {selectedOption?.label ?? ""}
        </span>
        <ChevronDown size={16} className="paid-media-filter-chip__chevron" />
      </button>
      {isOpen ? (
        <div className="paid-media-filter-dropdown__menu">
          {options.map(option => {
            const isSelected = option.value === value;
            return (
              <button
                key={option.value}
                type="button"
                className={`paid-media-filter-dropdown__item ${
                  isSelected ? "paid-media-filter-dropdown__item--selected" : ""
                }`}
                onClick={() => {
                  onChange(option.value);
                  setIsOpen(false);
                }}
              >
                <span className="paid-media-filter-dropdown__check">
                  {isSelected ? <Check size={16} /> : null}
                </span>
                <span>{option.label}</span>
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
};

interface GoogleCampaignsTableProps {
  googleSectionHelp: (_key: GoogleSectionHelpKey) => DefinitionTooltipInfo;
  selectedCampaignType: string;
  setSelectedCampaignType: Dispatch<SetStateAction<string>>;
  campaignTypeFilterOptions: Array<{ value: string; label: string }>;
  searchCampaigns: string;
  setSearchCampaigns: Dispatch<SetStateAction<string>>;
  setCurrentCampaignPage: Dispatch<SetStateAction<number>>;
  isExporting: boolean;
  exportCampaignsCsv: () => void;
  toggleCampaignSort: (_key: GoogleAdsCampaignSortKey) => void;
  campaignRows: CampaignSummary[];
  expandedCampaigns: Set<string>;
  toggleCampaignExpanded: (_campaignRowId: string) => void;
  formatCountMetric: (_value: number | null | undefined) => string;
  formatNullableCurrency: (_value: number | null | undefined) => string;
  formatNullablePercent: (_value: number | null | undefined) => string;
  formatConversionMetric: (_value: number | null | undefined) => string;
  formatNullableRoas: (_value: number | null | undefined) => string;
  formatCurrency: (
    _value: number,
    _options?: { minimumFractionDigits?: number; maximumFractionDigits?: number }
  ) => string;
  formatCurrencyShort: (_value: number) => string;
  currentCampaignPage: number;
  campaignTotalPages: number;
}

const GoogleCampaignsTable: FC<GoogleCampaignsTableProps> = ({
  googleSectionHelp,
  selectedCampaignType,
  setSelectedCampaignType,
  campaignTypeFilterOptions,
  searchCampaigns,
  setSearchCampaigns,
  setCurrentCampaignPage,
  isExporting,
  exportCampaignsCsv,
  toggleCampaignSort,
  campaignRows,
  expandedCampaigns,
  toggleCampaignExpanded,
  formatCountMetric,
  formatNullableCurrency,
  formatNullablePercent,
  formatConversionMetric,
  formatNullableRoas,
  formatCurrency,
  formatCurrencyShort,
  currentCampaignPage,
  campaignTotalPages,
}) => {
  const { t } = useTranslation();

  return (
    <DashboardDataTablePanel className="google-ads-table-panel--full">
      <DashboardDataTablePanelHeader>
        <div className="paid-media-panel__heading">
          <h3>{t("googleAdsReport.sections.campaignPerformance")}</h3>
          <DefinitionTooltip
            label={t("googleAdsReport.sections.campaignPerformance")}
            info={googleSectionHelp("campaignPerformance")}
            formulaTitle={t("webAnalytics.kpi.formula")}
          />
        </div>
        <div
          className="paid-media-table-toolbar"
          role="toolbar"
          aria-label={t("googleAdsReport.toolbars.campaignSearch")}
        >
          <div className="dashboard-section-header__control">
            <FilterDropdown
              value={selectedCampaignType}
              options={campaignTypeFilterOptions}
              onChange={value => {
                setSelectedCampaignType(value);
                setCurrentCampaignPage(1);
              }}
            />
          </div>
          <label className={`${dt.search} paid-media-table-toolbar__search`}>
            <Search size={16} />
            <input
              type="search"
              value={searchCampaigns}
              placeholder={t("googleAdsReport.placeholders.searchCampaigns")}
              onChange={event => {
                setSearchCampaigns(event.target.value);
                setCurrentCampaignPage(1);
              }}
            />
          </label>
          <button
            type="button"
            className="paid-media-export-btn"
            disabled={isExporting}
            onClick={() => void exportCampaignsCsv()}
          >
            <Download size={16} aria-hidden />
            {isExporting ? t("googleAdsReport.export.exporting") : t("googleAdsReport.export.csv")}
          </button>
        </div>
      </DashboardDataTablePanelHeader>
      <DashboardDataTableScroll>
        <DashboardDataTableTable className="paid-media-campaign-performance-table">
          <thead>
            <tr>
              <th>
                <DashboardDataTableSortButton onClick={() => toggleCampaignSort("name")}>
                  {t("googleAdsReport.campaignTable.campaign")}
                </DashboardDataTableSortButton>
              </th>
              {(
                [
                  ["campaignType", ""],
                  ["startDate", ""],
                  ["endDate", ""],
                  ["interactions", dt.number],
                  ["spend", dt.number],
                ] as const
              ).map(([column, alignClass]) => (
                <th key={column} className={alignClass || undefined}>
                  <span className={dt.thHelpRow}>
                    <DashboardDataTableSortButton
                      onClick={() => toggleCampaignSort(column)}
                    >
                      {t(`googleAdsReport.campaignTable.${column}`)}
                    </DashboardDataTableSortButton>
                    <DefinitionTooltip
                      label={t(`googleAdsReport.campaignTable.${column}`)}
                      info={getGoogleColumnTooltip(t, column)}
                      align="start"
                      formulaTitle={t("webAnalytics.kpi.formula")}
                    />
                  </span>
                </th>
              ))}
              <th className={dt.number}>
                <span className={dt.thHelpRow}>
                  {t("googleAdsReport.campaignTable.dailyBudget")}
                  <DefinitionTooltip
                    label={t("googleAdsReport.campaignTable.dailyBudget")}
                    info={getGoogleColumnTooltip(t, "dailyBudget")}
                    align="start"
                    formulaTitle={t("webAnalytics.kpi.formula")}
                  />
                </span>
              </th>
              {(
                [
                  ["impressions", dt.number],
                  ["clicks", dt.number],
                  ["ctr", dt.number],
                  ["cpc", dt.number],
                  ["conversions", dt.number],
                  ["cpa", dt.number],
                  ["roas", dt.number],
                ] as const
              ).map(([column, alignClass], columnIndex, columns) => (
                <th key={column} className={alignClass || undefined}>
                  <span className={dt.thHelpRow}>
                    <DashboardDataTableSortButton
                      onClick={() => toggleCampaignSort(column)}
                    >
                      {t(`googleAdsReport.campaignTable.${column}`)}
                    </DashboardDataTableSortButton>
                    <DefinitionTooltip
                      label={t(`googleAdsReport.campaignTable.${column}`)}
                      info={getGoogleColumnTooltip(t, column)}
                      align={columnIndex >= columns.length - 3 ? "end" : "start"}
                      formulaTitle={t("webAnalytics.kpi.formula")}
                    />
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {campaignRows.map(row => {
              const isExpanded = expandedCampaigns.has(row.id);
              const hasAdGroups = row.adGroups.length > 0;

              return (
                <Fragment key={row.id}>
                  <tr>
                    <td>
                      <div className="paid-media-campaign-cell">
                        {hasAdGroups ? (
                          <button
                            type="button"
                            className="paid-media-campaign-expand"
                            aria-expanded={isExpanded}
                            aria-label={t("googleAdsReport.campaignTable.toggleAdGroups")}
                            onClick={() => toggleCampaignExpanded(row.id)}
                          >
                            {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                          </button>
                        ) : (
                          <span className="paid-media-campaign-expand paid-media-campaign-expand--placeholder" />
                        )}
                        <div>
                          <strong>{row.campaignName}</strong>
                          {hasAdGroups ? (
                            <span className="paid-media-mini-tag paid-media-mini-tag--muted">
                              {t("googleAdsReport.campaignTable.adGroupCount", {
                                count: row.adGroups.length,
                              })}
                            </span>
                          ) : null}
                        </div>
                      </div>
                    </td>
                    <td>
                      {row.adNetworkType ? (
                        <span
                          className={`paid-media-mini-tag ${googleAdsAdNetworkChannelClass(row.adNetworkType)}`}
                        >
                          {row.adNetworkType}
                        </span>
                      ) : (
                        missingValue
                      )}
                    </td>
                    <td>{formatPaidMediaDateText(row.startDate) || missingValue}</td>
                    <td>{formatPaidMediaDateText(row.endDate) || missingValue}</td>
                    <td className={dt.number}>{formatCountMetric(row.interactions)}</td>
                    <td className={dt.number}>{formatNullableCurrency(row.spend)}</td>
                    <td className={dt.number}>
                      {displayFormattedCurrency(row.budgetFormattedValue)}
                    </td>
                    <td className={dt.number}>{formatCountMetric(row.impressions)}</td>
                    <td className={dt.number}>{formatCountMetric(row.clicks)}</td>
                    <td className={dt.number}>{formatNullablePercent(row.ctr)}</td>
                    <td className={dt.number}>{formatNullableCurrency(row.cpc)}</td>
                    <td className={dt.number}>{formatConversionMetric(row.conversions)}</td>
                    <td className={dt.number}>{formatNullableCurrency(row.cpa)}</td>
                    <td className={`${dt.number} ${getRoasToneClass(row.roas ?? 0)}`}>
                      {formatNullableRoas(row.roas)}
                    </td>
                  </tr>
                  {isExpanded
                    ? row.adGroups.map((adGroup, adGroupIndex) => {
                        const adGroupRow = mapAdGroupApiItem(adGroup, adGroupIndex);
                        return (
                          <tr
                            key={`${row.id}-${adGroupRow.id}`}
                            className="paid-media-table-subrow"
                          >
                            <td>
                              <span className="paid-media-table-subrow__label">
                                {adGroupRow.name}
                              </span>
                            </td>
                            <td>{missingValue}</td>
                            <td>{missingValue}</td>
                            <td>{missingValue}</td>
                            <td className={dt.number}>
                              {formatCountMetric(adGroupRow.interactions)}
                            </td>
                            <td className={dt.number}>{formatCurrency(adGroupRow.spend)}</td>
                            <td className={dt.number}>{missingValue}</td>
                            <td className={dt.number}>{formatCountMetric(adGroupRow.impressions)}</td>
                            <td className={dt.number}>{formatCountMetric(adGroupRow.clicks)}</td>
                            <td className={dt.number}>{formatNullablePercent(adGroupRow.ctr)}</td>
                            <td className={dt.number}>{formatCurrencyShort(adGroupRow.cpc)}</td>
                            <td className={dt.number}>{formatConversionMetric(adGroupRow.conversions)}</td>
                            <td className={dt.number}>{formatCurrencyShort(adGroupRow.cpa)}</td>
                            <td className={`${dt.number} ${getRoasToneClass(adGroupRow.roas)}`}>
                              {formatPaidMediaRoas(adGroupRow.roas)}
                            </td>
                          </tr>
                        );
                      })
                    : null}
                </Fragment>
              );
            })}
          </tbody>
        </DashboardDataTableTable>
      </DashboardDataTableScroll>
      <TablePagePagination
        currentPage={currentCampaignPage}
        totalPages={campaignTotalPages}
        onPrevious={() => setCurrentCampaignPage(page => Math.max(1, page - 1))}
        onNext={() =>
          setCurrentCampaignPage(page => Math.min(campaignTotalPages, page + 1))
        }
      />
    </DashboardDataTablePanel>
  );
};

export default GoogleCampaignsTable;
